import React from 'react';

import { Button, Dropdown, Empty, Menu, Table, Typography } from 'antd';
import { ConfigProvider } from 'antd';
import { DownOutlined } from '@ant-design/icons';

import 'fragments/follow-ups-list/follow-ups-list.scss';
import { FollowUpsListFragmentProps } from 'fragments/follow-ups-list/interfaces';
import { useFollowUpsListController } from 'fragments/follow-ups-list/follow-ups-list.controller';
import { FollowUpCardFragment } from 'fragments/follow-up-card/follow-up-card.fragment';
import { FollowUpCardFragmentProps } from 'fragments/follow-up-card/interfaces';
import FlexLoader from 'components/flex-loader/flex-loader.component';
import DownloadReceiptsModal from 'components/download-receipts-modal/download-receipts-modal.component';
import DownloadSettlementsModal from 'components/download-settlements-modal/download-settlements-modal.component';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import { GenerateFreeReceiptFragment } from 'fragments/generate-free-receipt/generate-free-receipt.fragment';
import { FilterInicioFragment } from 'fragments/filter-inicio/filter-inicio.fragment';
import RowWrapper from 'components/row-wrapper/row-wrapper.component';
import iconGenericReceipt from 'assets/icons-svg/icons-recibo-libre.svg';

export const FollowUpsListFragment: React.FC<FollowUpsListFragmentProps> = (props) => {
    const { useController = useFollowUpsListController } = props;
    const controller = useController();
    const { translate } = useTranslator();
    const { Column } = Table;

    // Menu children are built as a flat list because rc-menu only accepts Menu.Item / Menu.Divider
    // as direct children. A divider is inserted every time the action group changes, which keeps
    // the comprobante actions visually apart from the payment ones.
    const selectionMenuChildren: React.ReactNode[] = [];
    controller.selectionActions.forEach((action, index, actions) => {
        if (index > 0 && actions[index - 1].group !== action.group) {
            selectionMenuChildren.push(<Menu.Divider key={`${action.key}-divider`} />);
        }
        selectionMenuChildren.push(
            <Menu.Item key={action.key} onClick={action.onSelect}>
                {action.text}
            </Menu.Item>,
        );
    });

    // Rendered as the title of the column that sits right after the native selection column, so both
    // controls share the table header row with the select all checkbox. The flex wrapper spans the
    // whole header cell: dropdown trigger on the left, free receipt button on the right. Keeping the
    // free receipt button inside the cell (instead of floating it over the table) is what guarantees
    // it stays within the viewport on narrow screens, since the cell can never be wider than the table.
    const headerActions = (
        <div className={'header-actions'}>
            <Dropdown
                trigger={['click']}
                overlayClassName={'follow-ups-selection-overlay'}
                overlay={<Menu>{selectionMenuChildren}</Menu>}
            >
                <Button size={'middle'} type="text" className={'selection-actions'}>
                    <Typography.Text>
                        {translate({ key: 'follow-ups.generate-receipts-and-settlements' })}
                    </Typography.Text>
                    <DownOutlined />
                </Button>
            </Dropdown>
            {/* Below 992px this control is icon only: its label is hidden, so the accessible name
                comes from the aria-label instead of from the text node. */}
            <Button
                size={'middle'}
                type="text"
                onClick={controller.onGenerateFreeReceiptPressed}
                className={'generic-receipt'}
                aria-label={translate({ key: 'follow-ups.generate-free-receipt' })}
            >
                <img src={iconGenericReceipt} alt={''} />
                <Typography.Text>{translate({ key: 'follow-ups.generate-free-receipt' })}</Typography.Text>
            </Button>
        </div>
    );

    // Render
    return (
        <div className={'follow-ups-list'}>
            <DownloadReceiptsModal
                dto={controller.receiptsModalViewModel}
                isVisible={controller.isReceiptsModalVisible}
                onCancel={controller.onReceiptsModalCancel}
            />
            <DownloadSettlementsModal
                dto={controller.settlementsModalViewModel}
                isVisible={controller.isSettlementsModalVisible}
                onCancel={controller.onSettlementsModalCancel}
            />
            <GenerateFreeReceiptFragment
                isVisible={controller.isGenerateFreeReceiptVisible}
                onCancel={controller.onGenerateFreeReceiptCanceled}
                onGeneratePdfFreeReceiptAdded={controller.onGenerateFreeReceiptAdded}
            />
            <FilterInicioFragment
                offset={controller.currentPage}
                limit={controller.limitOfPagination}
                onFilterChange={controller.onFilterChange}
                onCleanFilter={controller.onCleanFilter}
            />
            <div>
                <ConfigProvider
                    renderEmpty={() => (
                        <>
                            {controller.isFiltering ? (
                                <Empty
                                    image={false}
                                    description={
                                        <Typography.Text>
                                            {translate({ key: 'follow-ups.filters-empty-result-message' })}
                                        </Typography.Text>
                                    }
                                />
                            ) : (
                                <Empty
                                    image={false}
                                    description={
                                        <Typography.Text>
                                            {translate({ key: 'follow-ups.you-do-not-have-followUps-loaded-yet' })}
                                            <br />
                                            {translate({ key: 'follow-ups.you-can-start-by' })}
                                            <Button type="link" size="small" href={'/contratos?addNew=true'}>
                                                {translate({ key: 'follow-ups.adding-a-contract' })}
                                            </Button>
                                        </Typography.Text>
                                    }
                                />
                            )}
                        </>
                    )}
                    componentSize={'large'}
                >
                    <Table
                        components={{ body: { row: RowWrapper } }}
                        pagination={{
                            pageSize: controller.limitOfPagination,
                            total: controller.total,
                            onChange: controller.onChangePage,
                            current: controller.currentPage,
                        }}
                        dataSource={controller.viewModels}
                        rowSelection={controller.rowSelection}
                        onRow={(item) => {
                            return item.header;
                        }}
                        rowKey={(item) => item.key}
                        loading={controller.isLoading}
                    >
                        <Column
                            title={headerActions}
                            render={(text: string, props: FollowUpCardFragmentProps) => (
                                <FollowUpCardFragment {...props} />
                            )}
                        />
                    </Table>
                </ConfigProvider>
            </div>
        </div>
    );
};
